import { PromiseCompletionSource } from "async/PromiseCompletionSource";
import { ICanvasScaler } from "by/gamefactory/canvas/ICanvasScaler";
import { BaseScalableView } from "by/gamefactory/cityversetycoon/scenes/common/BaseScalableView";
import { GameFont } from "by/gamefactory/cityversetycoon/scenes/common/GameFont";
import { ButtonWithScaleAnimation } from "by/gamefactory/cityversetycoon/scenes/common/components/ButtonWithScaleAnimation";
import { UIScrollBox } from "by/gamefactory/cityversetycoon/scenes/common/components/UIScrollBox";
import { CommonResourcesPackage } from "by/gamefactory/cityversetycoon/scenes/common/resources/CommonResourcesPackage";
import { FadeScreen } from "by/gamefactory/cityversetycoon/scenes/game/components/FadeScreen";
import { IDestroyOptions, Rectangle, Sprite, Text, TextStyle } from "pixi.js";
import { PriceHistoryResourcePackage } from "../resources/PriceHistoryResourcePackage";
import { PriceHistoryHeader } from "../components/PriceHistoryHeader";
import { PriceHistoryItem } from "../components/PriceHistoryItem";
import { PurchaseHistory } from "../../marketplace/main/models/building/PurchaseHistory";
import { VerticalScrollBar } from "../../../common/components/VerticalScrollBar";

export class PriceHistoryPopupView extends BaseScalableView<void> {
    private static readonly HEADER_TEXT = "BUILDING PRICE HISTORY";

    private fadeScreen: FadeScreen;
    private shape: Sprite;
    private header: PriceHistoryHeader;
    private headerText: Text;
    private scrollBox: UIScrollBox;
    private scrollBar: VerticalScrollBar;
    private backButton: ButtonWithScaleAnimation;

    public constructor(
        operationControls: PromiseCompletionSource<void>,
        private readonly commonResources: CommonResourcesPackage,
        private readonly priceHistoryResources: PriceHistoryResourcePackage,
        canvasScaler: ICanvasScaler,
        private readonly purchaseHistory: Array<PurchaseHistory>,
    ) {
        super(operationControls, canvasScaler);
        this.build();
        this.backButton.onUp.add(this.backButtonClickHandle, this);
        this.scrollBox.onScroll.add(this.onScroll, this);
        this.layout(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
    }

    public async start(): Promise<void> {
        await this.fadeScreen.fadeIn();
    }

    private build(): void {
        this.fadeScreen = new FadeScreen(0.1, 0, 0.85, this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.fadeScreen.eventMode = "static";

        this.shape = new Sprite(this.priceHistoryResources.assets_views_price_history_shape_png);
        this.headerText = new Text(PriceHistoryPopupView.HEADER_TEXT, new HeaderTextStyle());
        this.header = new PriceHistoryHeader(this.priceHistoryResources);

        this.addChild(this.headerText);
        this.scrollBox = new UIScrollBox({
            width: this.shape.width,
            height: 1050,
            type: "vertical",
            elementsMargin: 38
        });

        this.createPriceHistoryItems();

        this.scrollBar = new VerticalScrollBar({
            lift: this.priceHistoryResources.assets_views_price_history_scroll_bar_lift_png,
            line: this.priceHistoryResources.assets_views_price_history_scroll_bar_bg_png
        }, 1050, ((this.scrollBox.items[0]?.height ?? 0) + 38) * this.scrollBox.items.length - 1, 0, -5);

        this.backButton = new ButtonWithScaleAnimation(this.commonResources.assets_common_ui_x_png);
        this.backButton.hitArea = new Rectangle(-this.backButton.width * 0.5, -this.backButton.height, this.backButton.width * 2, this.backButton.height * 2);
        this.addChild(this.backButton);

        this.shape.addChild(this.headerText, this.header, this.scrollBox, this.scrollBar);
        this.addChild(this.fadeScreen, this.shape, this.backButton);

        if (this.purchaseHistory.length < 10) {
            this.scrollBox.setScrollable(false);
            this.scrollBox.setWheelScrollable(false);
        }
    }

    private createPriceHistoryItems(): void {
        for (const historyItem of this.purchaseHistory) {
            const priceHistoryItem = new PriceHistoryItem(this.priceHistoryResources, historyItem);
            this.scrollBox.addItem(priceHistoryItem);
        }
    }

    private onScroll(value: number): void {
        this.scrollBar.updateScroll(value);
    }

    private layout(width: number, height: number): void {
        this.shape.position.set(width * 0.5 - this.shape.width * 0.5, height * 0.5 - this.shape.height * 0.5 - 46);
        this.headerText.position.set(166, 55);
        this.header.position.set(this.shape.width * 0.5 - this.header.width * 0.5, 164);
        this.backButton.position.set(width * 0.5 - this.backButton.width * 0.5, 1575);
        this.scrollBox.position.set(0, 303);
        this.scrollBar.position.set(755, 300);
    }

    private async backButtonClickHandle(): Promise<void> {
        await this.fadeScreen.fadeOut();
        this.operationControls.complete();
    }

    public destroy(options?: boolean | IDestroyOptions): void {
        this.backButton.onUp.remove(this.backButtonClickHandle, this);
        this.scrollBox.onScroll.remove(this.onScroll, this);
        super.destroy(options);
    }
}

class HeaderTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            fill: [
                "#01ffbe",
                "#1aff8c",
                "#28f177"
            ],
            fillGradientType: 1,
            fillGradientStops: [
                0.3
            ],
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 60,
        }));
    }
}
